import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, ShieldAlert, ShieldX, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui/PageHeader.js";
import { StatusBadge } from "@/components/ui/StatusBadge.js";
import { api } from "@/lib/api.js";
import { relativeTime } from "@/lib/format.js";

interface LastCheck {
  status: "OK" | "WARN" | "DOWN";
  note: string | null;
  checkedAt: string;
}
interface MetaChannel {
  idcanal: number;
  idcompany: number;
  companyName: string | null;
  name: string;
  tipo: number;
  estado: number | null;
  hasToken: boolean;
  lastCheck: LastCheck | null;
}

interface VerifyResult {
  idcanal: number;
  idcompany: number;
  tipo: number;
  status: "OK" | "WARN" | "DOWN";
  isValid: boolean;
  reason: string;
  humanMessage: string;
  expiresAt: string | null;
  dataAccessExpiresAt: string | null;
  type: string | null;
  scopes: string[];
  checkedAt: string;
}

const TIPO_LABEL: Record<number, string> = {
  0: "Facebook",
  1: "Instagram",
};

function reasonIcon(status: string) {
  if (status === "OK") return <ShieldCheck size={14} className="text-emerald-600" />;
  if (status === "WARN") return <ShieldAlert size={14} className="text-amber-600" />;
  return <ShieldX size={14} className="text-rose-600" />;
}

export function ChannelHealthPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "down" | "warn">("all");
  const [results, setResults] = useState<Record<number, VerifyResult>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["meta-channels"],
    queryFn: () => api.get<{ items: MetaChannel[] }>("/channel-health/meta-channels"),
  });

  const verifyOne = useMutation({
    mutationFn: (idcanal: number) => api.post<VerifyResult>(`/channel-health/verify/${idcanal}`),
    onSuccess: (r) => {
      setResults((prev) => ({ ...prev, [r.idcanal]: r }));
      if (r.status === "DOWN") toast.error(r.humanMessage);
      else if (r.status === "WARN") toast.warning(r.humanMessage);
      else toast.success("Token válido");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const verifyAll = useMutation({
    mutationFn: () =>
      api.post<{ total: number; ok: number; warn: number; down: number; items: VerifyResult[] }>(
        "/channel-health/verify-all",
      ),
    onSuccess: (r) => {
      const map: Record<number, VerifyResult> = {};
      for (const it of r.items) map[it.idcanal] = it as VerifyResult;
      setResults((prev) => ({ ...prev, ...map }));
      toast.success(`Verificados ${r.total} · OK ${r.ok} · WARN ${r.warn} · DOWN ${r.down}`);
      qc.invalidateQueries({ queryKey: ["meta-channels"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Status efectivo: results (manual verify hecho en esta sesión) o lastCheck (pre-cargado por cron)
  function effectiveStatus(c: MetaChannel):
    | { status: "OK" | "WARN" | "DOWN"; note: string; checkedAt: string; fromSession: boolean }
    | null {
    const r = results[c.idcanal];
    if (r) {
      return { status: r.status, note: r.humanMessage, checkedAt: r.checkedAt, fromSession: true };
    }
    if (c.lastCheck) {
      return {
        status: c.lastCheck.status,
        note: c.lastCheck.note ?? "",
        checkedAt: c.lastCheck.checkedAt,
        fromSession: false,
      };
    }
    return null;
  }

  const filtered = useMemo(() => {
    const items = data?.items ?? [];
    const q = search.trim().toLowerCase();
    return items.filter((c) => {
      if (q) {
        const hay = `${c.idcanal} ${c.name} ${c.companyName ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filter !== "all") {
        const eff = effectiveStatus(c);
        if (!eff) return false;
        if (filter === "down" && eff.status !== "DOWN") return false;
        if (filter === "warn" && eff.status !== "WARN") return false;
      }
      return true;
    });
  }, [data, search, filter, results]);

  const summary = useMemo(() => {
    const items = data?.items ?? [];
    let ok = 0,
      warn = 0,
      down = 0,
      unchecked = 0;
    for (const c of items) {
      const eff = effectiveStatus(c);
      if (!eff) unchecked++;
      else if (eff.status === "OK") ok++;
      else if (eff.status === "WARN") warn++;
      else down++;
    }
    return { ok, warn, down, unchecked };
  }, [data, results]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Salud de canales"
        description="Verifica el estado de tokens Meta (Facebook, Instagram, Meta Cloud) y detecta sesiones perdidas antes de que el cliente llame."
        actions={
          <button
            className="btn-primary"
            onClick={() => verifyAll.mutate()}
            disabled={verifyAll.isPending}
          >
            {verifyAll.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Verificando…
              </>
            ) : (
              <>
                <RefreshCw size={14} className="mr-2" />
                Verificar todos
              </>
            )}
          </button>
        }
      />

      <div className="grid grid-cols-5 gap-4">
        <div className="card p-4">
          <p className="text-xs text-slate-500 uppercase">Total FB/IG</p>
          <p className="text-2xl font-semibold">{data?.items.length ?? "—"}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-slate-500 uppercase">OK</p>
          <p className="text-2xl font-semibold text-emerald-600">{summary.ok}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-slate-500 uppercase">Por vencer</p>
          <p className="text-2xl font-semibold text-amber-600">{summary.warn}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-slate-500 uppercase">Caídos</p>
          <p className="text-2xl font-semibold text-rose-600">{summary.down}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-slate-500 uppercase">Sin verificar</p>
          <p className="text-2xl font-semibold text-slate-500">{summary.unchecked}</p>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <input
          className="input max-w-sm"
          placeholder="Buscar por company, ID o nombre…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex gap-1">
          {(["all", "warn", "down"] as const).map((f) => (
            <button
              key={f}
              className={`btn ${filter === f ? "btn-primary" : "btn-secondary"}`}
              onClick={() => setFilter(f)}
            >
              {f === "all" ? "Todos" : f === "warn" ? "Por vencer" : "Caídos"}
            </button>
          ))}
        </div>
        <span className="ml-auto text-xs text-slate-500 self-center">
          Mostrando {filtered.length} de {data?.items.length ?? 0}
        </span>
      </div>

      {isLoading ? (
        <div className="text-slate-500">Cargando canales…</div>
      ) : filtered.length === 0 ? (
        <div className="card p-12 text-center text-sm text-slate-500">
          Sin resultados para el filtro actual.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {filtered.map((c) => {
            const eff = effectiveStatus(c);
            const manual = results[c.idcanal];
            return (
              <div key={c.idcanal} className="card p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500 uppercase mono">
                      {TIPO_LABEL[c.tipo] ?? `TIPO ${c.tipo}`} · canal {c.idcanal}
                    </p>
                    <p className="font-semibold truncate">{c.name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {c.companyName ?? `Company ${c.idcompany}`}
                    </p>
                  </div>
                  {eff ? (
                    <StatusBadge value={eff.status} />
                  ) : (
                    <span className="badge-neutral">Sin verificar</span>
                  )}
                </div>

                {!c.hasToken && (
                  <div className="text-xs rounded-md bg-slate-100 border border-slate-200 p-2 text-slate-600">
                    Canal sin token configurado.
                  </div>
                )}

                {eff && (
                  <div
                    className={`text-xs rounded-md p-2 border ${
                      eff.status === "OK"
                        ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                        : eff.status === "WARN"
                          ? "bg-amber-50 border-amber-200 text-amber-800"
                          : "bg-rose-50 border-rose-200 text-rose-800"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {reasonIcon(eff.status)}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium mb-1">{eff.note || (eff.status === "OK" ? "Token válido" : "")}</p>
                        <div className="flex gap-3 text-[11px] opacity-80 flex-wrap">
                          {manual && <span>Motivo: {manual.reason}</span>}
                          {manual?.expiresAt && (
                            <span>Expira: {relativeTime(manual.expiresAt)}</span>
                          )}
                          <span>
                            {eff.fromSession ? "Verificado ahora" : "Check automático"} ·{" "}
                            {relativeTime(eff.checkedAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    className="btn-secondary flex-1"
                    onClick={() => verifyOne.mutate(c.idcanal)}
                    disabled={verifyOne.isPending || !c.hasToken}
                  >
                    {verifyOne.isPending && verifyOne.variables === c.idcanal ? (
                      <>
                        <Loader2 size={12} className="animate-spin mr-1" />
                        Verificando…
                      </>
                    ) : (
                      <>
                        <ShieldCheck size={12} className="mr-1" />
                        Verificar ahora
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-slate-400">
        Verificación automática cada 15 min (batch de 10 canales) vía Cloud Scheduler. También
        puedes forzar una verificación manual con los botones. El endpoint consulta{" "}
        <span className="mono">debug_token</span> de Meta Graph para detectar cambio de
        contraseña, desinstalación de app o expiración.
      </p>
    </div>
  );
}
